import React, { useCallback, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import Select from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import type { SelectProps } from '@amzn/awsui-components-console';
import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
} from '../types';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  MAX_STRINGS_PER_GROUP,
  SOURCE_TEXT_CHAR_LIMIT,
  STRING_ID_MAX_LENGTH,
} from '../constants';
import {
  getEntryErrors,
  getLimitError,
  isEntryValid,
} from './createStringValidation';
import styles from '../StringGroupDetail.module.scss';
import RequiredAsterisk from 'src/components/common/util/FormHelpers';
import NoPiiNotice from 'src/common/ui/NoPiiNotice';
import { recordRumEvent } from 'src/common/rum/rum';
import { RUM_CLICK_EVENT } from 'src/common/constants';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';
import {
  INLINE_MARKUP_LABEL_IDS,
  MESSAGE_FORMAT_LABEL_IDS,
} from 'src/common/formatLabels';

interface StringEntry {
  stringId: string;
  sourceText: string;
  contextNote: string;
  inlineMarkupType: InlineMarkupType;
  messageFormatType: MessageFormatType;
}

/**
 * Which of an entry's fields have had their error revealed. A field's error is
 * shown as soon as it is touched — so validation surfaces live as the user
 * types rather than only after Submit — while unrevealed fields stay quiet so a
 * freshly opened form never opens covered in errors. Submit reveals every field
 * of the entries present at that moment (see {@link ALL_TOUCHED}); entries added
 * afterward start unrevealed and stay quiet until they too are touched.
 */
type TouchedFields = {
  stringId?: boolean;
  sourceText?: boolean;
  contextNote?: boolean;
};

/** Every validated field revealed at once — the state Submit forces per entry. */
const ALL_TOUCHED: TouchedFields = {
  stringId: true,
  sourceText: true,
  contextNote: true,
};

interface CreateStringModalProps {
  visible: boolean;
  sourceLocale: string;
  existingStringIds: string[];
  draftAddedStringIds: string[];
  /**
   * IDs already marked for deletion in the draft. They are submitted in the same
   * changeset as these additions, so they free up headroom: the backend caps the
   * RESULTING group size (`baseline - deleted + added`) at
   * {@link MAX_STRINGS_PER_GROUP}. Deliberately NOT excluded from
   * `existingStringIds`, which still guards ID uniqueness — reusing the ID of a
   * string being deleted in the same submission would put it in both `deleted`
   * and `added`.
   */
  draftDeletedStringIds: string[];
  onSubmit: (strings: DraftStringInput[]) => void;
  onDismiss: () => void;
  onSuccess: (count: number) => void;
}

const EMPTY_ENTRY: StringEntry = {
  stringId: '',
  sourceText: '',
  contextNote: '',
  inlineMarkupType: InlineMarkupType.PlainText,
  messageFormatType: MessageFormatType.None,
};

/**
 * Inline markup choices, offered as a dropdown rather than radios: TRANSCOM is
 * adding many more markup types, and a growing radio list would dominate the
 * form (and become unusable on a narrow / mobile viewport). A Select stays a
 * single row no matter how long the option list grows.
 */
const inlineMarkupOptions = (t: TranslateFn): SelectProps.Option[] => [
  {
    value: InlineMarkupType.PlainText,
    label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.PlainText]),
  },
  {
    value: InlineMarkupType.Html,
    label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.Html]),
  },
  {
    value: InlineMarkupType.Markdown,
    label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.Markdown]),
  },
];

/** Message format choices — a dropdown for the same reason as the markup list. */
const messageFormatOptions = (t: TranslateFn): SelectProps.Option[] => [
  {
    value: MessageFormatType.None,
    label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.None]),
  },
  {
    value: MessageFormatType.HashGet,
    label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.HashGet]),
  },
  {
    value: MessageFormatType.AmazonMessageFormat,
    label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.AmazonMessageFormat]),
  },
  {
    value: MessageFormatType.Mustache,
    label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.Mustache]),
  },
];

/** Resolves a Select option object from a stored enum value. */
function optionForValue(
  options: SelectProps.Option[],
  value: string,
): SelectProps.Option | null {
  return options.find((option) => option.value === value) ?? null;
}

/** Deliberately NOT a resource: see the comment at the use site. */
const EXAMPLE_STRING_IDS = 'BUTTON_LABEL, CATEGORY_TITLE';

const CreateStringModal: React.FC<CreateStringModalProps> = ({
  visible,
  sourceLocale,
  existingStringIds,
  draftAddedStringIds,
  draftDeletedStringIds,
  onSubmit,
  onDismiss,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<StringEntry[]>([{ ...EMPTY_ENTRY }]);
  const [touched, setTouched] = useState<TouchedFields[]>([{}]);

  // Headroom mirrors the backend's cap on the RESULTING group size
  // (`baseline - deleted + added` must not exceed MAX_STRINGS_PER_GROUP), so
  // strings already marked for deletion in this changeset free up room. Floored at
  // 0 because the draft's deletions can never exceed the strings that exist.
  const existingTotal = Math.max(
    existingStringIds.length +
      draftAddedStringIds.length -
      draftDeletedStringIds.length,
    0,
  );
  const remainingSlots = Math.max(MAX_STRINGS_PER_GROUP - existingTotal, 0);

  // Errors are derived from the current entries on every render, so a field's
  // message reflects the latest keystroke the moment it changes.
  const allErrors = entries.map((_, index) =>
    getEntryErrors(t, index, entries, existingStringIds, draftAddedStringIds),
  );
  const limitError = getLimitError(
    t,
    entries.length,
    existingTotal,
    remainingSlots,
  );

  /** True when a field's error should be visible: once its entry is touched or Submit-revealed. */
  const showFieldError = (index: number, field: keyof TouchedFields): boolean =>
    !!touched[index]?.[field];

  const resetForm = useCallback(() => {
    setEntries([{ ...EMPTY_ENTRY }]);
    setTouched([{}]);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [onDismiss, resetForm]);

  const updateEntry = (
    index: number,
    field: keyof StringEntry,
    value: string,
  ) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Mark the edited field touched so its (freshly recomputed) error becomes
    // visible on the fly. The three validated fields share keys with the entry,
    // so this covers stringId/sourceText/contextNote; the radio fields have no
    // validation and simply fall through.
    setTouched((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: true };
      return updated;
    });
  };

  const addEntry = () => {
    recordRumEvent(RUM_CLICK_EVENT.AddAnotherString);
    // Nearly every string group uses one markup + format for all its strings,
    // so seed a new entry from the previous one's choices rather than resetting
    // to the defaults. The other fields (id/text/note) are per-string, so they
    // stay empty. Falls back to the defaults when, defensively, there is no
    // previous entry.
    setEntries((prev) => {
      const last = prev[prev.length - 1];
      return [
        ...prev,
        {
          ...EMPTY_ENTRY,
          inlineMarkupType:
            last?.inlineMarkupType ?? EMPTY_ENTRY.inlineMarkupType,
          messageFormatType:
            last?.messageFormatType ?? EMPTY_ENTRY.messageFormatType,
        },
      ];
    });
    setTouched((prev) => [...prev, {}]);
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setTouched((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitAll = () => {
    // Reveal every field of the entries present now (entries added later stay
    // quiet until touched), then block submission if anything is invalid.
    setTouched(entries.map(() => ({ ...ALL_TOUCHED })));

    const allValid = allErrors.every(isEntryValid);
    if (!allValid || limitError) {
      return;
    }

    const drafts: DraftStringInput[] = entries.map((entry) => ({
      stringId: entry.stringId.trim(),
      sourceLocale,
      // Trimmed to match what validation checked, so surrounding whitespace is
      // never persisted into the draft.
      sourceText: entry.sourceText.trim(),
      contextNote: entry.contextNote.trim(),
      inlineMarkupType: entry.inlineMarkupType,
      messageFormatType: entry.messageFormatType,
      variants: [],
    }));

    // Add each string to draft state
    for (const draft of drafts) {
      onSubmit([draft]);
    }

    const count = drafts.length;
    resetForm();
    onDismiss();
    onSuccess(count);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={t('sgd-create-strings')}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              {t('string-cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmitAll}>
              {t('sgd-submit-all')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <NoPiiNotice />

        {limitError && <Alert type="error">{limitError}</Alert>}

        {entries.map((entry, index) => (
          <div key={index} className={styles.stringEntryCard}>
            <div className={styles.stringEntryHeader}>
              <Box variant="h4">String {index + 1}</Box>
              {entries.length > 1 && (
                <Button variant="link" onClick={() => removeEntry(index)}>
                  {t('sgd-remove')}
                </Button>
              )}
            </div>

            <ColumnLayout columns={2}>
              <FormField
                label={
                  <>
                    {t('string-field-string-id')}
                    <RequiredAsterisk />{' '}
                  </>
                }
                constraintText={t('field-character-limit-sentence', {
                  limit: STRING_ID_MAX_LENGTH,
                })}
                errorText={
                  showFieldError(index, 'stringId')
                    ? allErrors[index]?.stringId
                    : undefined
                }
              >
                <Input
                  ariaRequired={true}
                  value={entry.stringId}
                  // Identifiers, not prose: STRING_ID_PATTERN allows only
                  // A-Z, 0-9, hyphen and underscore, so a translated example
                  // would be an invalid one.
                  placeholder={EXAMPLE_STRING_IDS}
                  onChange={({ detail }) =>
                    updateEntry(index, 'stringId', detail.value)
                  }
                />
              </FormField>
              <FormField
                label={t('string-field-source-locale')}
                constraintText={t('sgd-source-locale-fixed')}
              >
                <Input value={sourceLocale} disabled />
              </FormField>
            </ColumnLayout>

            <FormField
              label={
                <>
                  {t('string-field-source-text')}
                  <RequiredAsterisk />{' '}
                </>
              }
              constraintText={t('field-character-limit', {
                limit: SOURCE_TEXT_CHAR_LIMIT,
              })}
              errorText={
                showFieldError(index, 'sourceText')
                  ? allErrors[index]?.sourceText
                  : undefined
              }
            >
              <Textarea
                value={entry.sourceText}
                placeholder={t('sgd-source-text-placeholder')}
                onChange={({ detail }) =>
                  updateEntry(index, 'sourceText', detail.value)
                }
              />
            </FormField>

            <FormField
              label={
                <>
                  {t('string-field-context-note')}
                  <RequiredAsterisk />{' '}
                </>
              }
              errorText={
                showFieldError(index, 'contextNote')
                  ? allErrors[index]?.contextNote
                  : undefined
              }
              constraintText={t('field-character-limit', {
                limit: CONTEXT_NOTE_CHAR_LIMIT,
              })}
            >
              <Textarea
                value={entry.contextNote}
                placeholder={t('string-context-note-help')}
                rows={2}
                onChange={({ detail }) =>
                  updateEntry(index, 'contextNote', detail.value)
                }
              />
            </FormField>

            <FormField
              label={
                <>
                  {t('sgd-field-inline-markup')}
                  <RequiredAsterisk />{' '}
                </>
              }
            >
              <Select
                ariaRequired={true}
                selectedOption={optionForValue(
                  inlineMarkupOptions(t),
                  entry.inlineMarkupType,
                )}
                options={inlineMarkupOptions(t)}
                ariaLabel={t('sgd-create-string-inline-markup-aria', {
                  position: index + 1,
                })}
                onChange={({ detail }) =>
                  updateEntry(
                    index,
                    'inlineMarkupType',
                    detail.selectedOption.value as InlineMarkupType,
                  )
                }
              />
            </FormField>

            <FormField label={t('string-field-message-format-required')}>
              <Select
                selectedOption={optionForValue(
                  messageFormatOptions(t),
                  entry.messageFormatType,
                )}
                options={messageFormatOptions(t)}
                ariaLabel={t('sgd-create-string-message-format-aria', {
                  position: index + 1,
                })}
                onChange={({ detail }) =>
                  updateEntry(
                    index,
                    'messageFormatType',
                    detail.selectedOption.value as MessageFormatType,
                  )
                }
              />
            </FormField>
          </div>
        ))}

        <Button
          variant="normal"
          iconName="add-plus"
          onClick={addEntry}
          disabled={entries.length >= remainingSlots}
          fullWidth
        >
          {t('sgd-add-another-string')}
        </Button>

        <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
          {entries.length} string(s) ready to submit
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default CreateStringModal;
